import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flag, ChevronRight, ChevronDown } from "lucide-react";
import { api } from "../../lib/api";
import StepDetailBody from "./StepDetailBody";

const money = (n) => (typeof n === "number" && n > 0 ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : null);
const TONE = { High: "text-[#1e7d5a]", Medium: "text-[#a3823f]", Low: "text-[#1e3d3a]/50" };
const EFFORT = { Low: "text-[#1e7d5a]", Medium: "text-[#a3823f]", High: "text-[#b23a48]" };

/**
 * "How we get there · prioritized" — a goal plus prioritized action boxes.
 * When `finRef` is provided, each box is clickable and expands into a
 * step-by-step execution guide + saved checklist (how do I actually DO this).
 * Progress is persisted server-side keyed by finRef so it survives reloads.
 */
export default function ActionPlan({ plan, testid, finRef, businessName, chartKey, goalContext }) {
  const [openIdx, setOpenIdx] = useState(null);
  const [opened, setOpened] = useState({});
  if (!plan || (!plan.goal && !(plan.actions || []).length)) return null;
  const actions = (plan.actions || []).slice(0, 4);
  const interactive = !!finRef;
  const ref = finRef ? `${finRef}:${chartKey || testid}` : null;

  const toggle = (i) => {
    setOpenIdx((cur) => (cur === i ? null : i));
    setOpened((o) => ({ ...o, [i]: true }));
  };

  return (
    <div data-testid={testid} className="mt-3 rounded-2xl border border-[#1e3d3a]/10 bg-[#f7f5ee] p-4">
      <p className="text-[9px] uppercase tracking-[0.22em] text-[#1e3d3a]/50">How we get there · prioritized</p>
      {plan.goal && (
        <p className="mt-1.5 text-sm text-[#1e3d3a] flex items-start gap-1.5"><Flag className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#c9a961]" /><span><span className="text-[#1e3d3a]/50">Goal:</span> {plan.goal}</span></p>
      )}
      <ol className="mt-3 space-y-2">
        {actions.map((a, i) => {
          const first = i === 0;
          const isOpen = openIdx === i;
          const context = `Goal: ${plan.goal || goalContext || ""}. Priority action #${i + 1}${a.how ? `. Method hint: ${a.how}` : ""}${a.est_value ? `. Estimated value: $${a.est_value}/yr` : ""}.`;
          return (
            <li key={i} data-testid={`${testid}-action-${i}`}
              className={`rounded-xl overflow-hidden ${first ? "bg-[#1e7d5a]/10 border border-[#1e7d5a]/30" : "bg-white border border-[#1e3d3a]/8"}`}>
              <button
                type="button"
                data-testid={`${testid}-action-${i}-toggle`}
                onClick={interactive ? () => toggle(i) : undefined}
                className={`w-full text-left px-3 py-2.5 ${interactive ? "hover:bg-[#c9a961]/8 transition-colors cursor-pointer" : "cursor-default"}`}
              >
                <div className="flex items-start gap-2">
                  <span className={`mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-[8px] uppercase tracking-[0.16em] font-bold ${first ? "bg-[#1e7d5a] text-white" : "bg-[#eae7de] text-[#1e3d3a]/60"}`}>
                    {first ? "Act first" : `#${i + 1}`}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#1e3d3a] flex items-center gap-1.5">
                      {a.title}
                      {interactive && (isOpen
                        ? <ChevronDown className="h-3.5 w-3.5 text-[#c9a961] shrink-0" />
                        : <ChevronRight className="h-3.5 w-3.5 text-[#c9a961] shrink-0" />)}
                    </p>
                    {a.how && <p className="mt-0.5 text-xs leading-relaxed text-[#1e3d3a]/70">{a.how}</p>}
                    <p className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] uppercase tracking-[0.12em]">
                      {a.impact && <span className={TONE[a.impact] || TONE.Medium}>Impact {a.impact}</span>}
                      {a.effort && <span className={EFFORT[a.effort] || EFFORT.Medium}>Effort {a.effort}</span>}
                      {a.timeframe && <span className="text-[#1e3d3a]/45">{a.timeframe}</span>}
                      {money(a.est_value) && <span className="text-[#1e3d3a]">≈ {money(a.est_value)}/yr</span>}
                      {interactive && !isOpen && <span className="text-[#1e7d5a]">See how →</span>}
                    </p>
                  </div>
                </div>
              </button>
              {interactive && (
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      data-testid={`${testid}-action-${i}-panel`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="px-3 pb-3 pt-1 border-t border-[#1e3d3a]/8 bg-white/40">
                        <StepDetailBody
                          active={!!opened[i]}
                          testid={`${testid}-action-${i}`}
                          loadDetail={() => api.post("/prospect/action-detail", {
                            ref, action_index: i, step_text: a.title, business_name: businessName || "", context,
                          }).then((r) => r.data)}
                          saveItem={(itemIndex, done) => api.put("/prospect/action-detail/checklist", {
                            ref, action_index: i, item_index: itemIndex, done,
                          })}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </li>
          );
        })}
      </ol>
      {plan.first_move_reason && (
        <p className="mt-2.5 flex items-start gap-1 text-[11px] italic text-[#1e3d3a]/55"><ChevronRight className="mt-0.5 h-3 w-3 shrink-0 text-[#1e7d5a]" /> Why #1 first: {plan.first_move_reason}</p>
      )}
    </div>
  );
}
